import { useState } from "react";
import { useFetch } from "../../hooks/useFetch";

// --- Types (mirrors server-side definitions) ---

type TargetingCondition = {
	attribute: string;
	operator: "in" | "percentage";
	values?: string[];
	percentage?: number;
};

type TargetingRule = {
	conditions: TargetingCondition[];
	value: unknown;
};

type FlagDefinition = {
	enabled: boolean;
	defaultValue: unknown;
	targeting?: TargetingRule[];
};

type TargetingContext = {
	targetingKey: string;
	tenantId?: string;
	[key: string]: unknown;
};

// --- Client-side targeting engine (duplicated from targeting.ts) ---

function fnv1aHash(input: string): number {
	let hash = 0x811c9dc5;
	for (let i = 0; i < input.length; i++) {
		hash ^= input.charCodeAt(i);
		hash = Math.imul(hash, 0x01000193);
	}
	hash = hash >>> 0;
	return hash % 10000;
}

function conditionMatches(
	condition: TargetingCondition,
	context: TargetingContext,
	flagKey: string,
): boolean {
	if (condition.operator === "in") {
		const contextValue = String(context[condition.attribute] ?? "");
		return condition.values?.includes(contextValue) ?? false;
	}
	if (condition.operator === "percentage") {
		const pct = condition.percentage ?? 0;
		const hashInput = `${flagKey}:${context.targetingKey}`;
		const bucket = fnv1aHash(hashInput);
		return bucket < pct * 100;
	}
	return false;
}

function evaluateTargeting(
	rules: TargetingRule[],
	context: TargetingContext,
	flagKey: string,
): { match: true; value: unknown } | { match: false } {
	for (const rule of rules) {
		if (rule.conditions.every((c) => conditionMatches(c, context, flagKey))) {
			return { match: true, value: rule.value };
		}
	}
	return { match: false };
}

// --- Form types with stable IDs for React keys ---

type FormCondition = TargetingCondition & { id: string };
type FormRule = { id: string; conditions: FormCondition[]; value: unknown };

let nextId = 0;
function uid(): string {
	nextId += 1;
	return `_${nextId}`;
}

const FLAG_KEY_REGEX = /^[a-z][a-z0-9._-]*$/;

function emptyCondition(): FormCondition {
	return { id: uid(), attribute: "", operator: "in", values: [] };
}

function emptyRule(): FormRule {
	return { id: uid(), conditions: [emptyCondition()], value: true };
}

function toFormRules(rules: TargetingRule[]): FormRule[] {
	return rules.map((r) => ({
		id: uid(),
		conditions: r.conditions.map((c) => ({ ...c, id: uid() })),
		value: r.value,
	}));
}

function toApiRules(rules: FormRule[]): TargetingRule[] {
	return rules.map(({ conditions, value }) => ({
		conditions: conditions.map(({ id: _, ...rest }) => rest),
		value,
	}));
}

// --- Component ---

export function AdminFlagsPage() {
	const { data, loading, error, refetch } =
		useFetch<Record<string, FlagDefinition>>("/api/admin/flags");
	const [actionLoading, setActionLoading] = useState<string | null>(null);

	// Modal state
	const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
	const [editKey, setEditKey] = useState<string | null>(null);
	const [modalError, setModalError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	// Modal form fields
	const [formKey, setFormKey] = useState("");
	const [formEnabled, setFormEnabled] = useState(true);
	const [formDefaultValue, setFormDefaultValue] = useState("true");
	const [formRules, setFormRules] = useState<FormRule[]>([]);

	// Evaluation preview
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewFlagKey, setPreviewFlagKey] = useState("");
	const [previewTargetingKey, setPreviewTargetingKey] = useState("");
	const [previewTenantId, setPreviewTenantId] = useState("");
	const [previewResult, setPreviewResult] = useState<string | null>(null);

	const flags = data ? Object.entries(data) : [];

	// --- Toggle ---

	async function handleToggle(key: string, current: boolean) {
		setActionLoading(key);
		try {
			await fetch(`/api/admin/flags/${key}`, {
				method: "PUT",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ enabled: !current }),
			});
			refetch();
		} finally {
			setActionLoading(null);
		}
	}

	// --- Delete ---

	async function handleDelete(key: string) {
		if (!window.confirm(`Delete flag "${key}"?`)) return;
		setActionLoading(key);
		try {
			await fetch(`/api/admin/flags/${key}`, {
				method: "DELETE",
				credentials: "include",
			});
			refetch();
		} finally {
			setActionLoading(null);
		}
	}

	// --- Modal open/close ---

	function openCreate() {
		setModalMode("create");
		setEditKey(null);
		setFormKey("");
		setFormEnabled(true);
		setFormDefaultValue("true");
		setFormRules([]);
		setModalError(null);
	}

	function openEdit(key: string) {
		if (!data) return;
		const flag = data[key];
		setModalMode("edit");
		setEditKey(key);
		setFormKey(key);
		setFormEnabled(flag.enabled);
		setFormDefaultValue(JSON.stringify(flag.defaultValue));
		setFormRules(flag.targeting ? toFormRules(flag.targeting) : []);
		setModalError(null);
	}

	function closeModal() {
		setModalMode(null);
		setEditKey(null);
		setModalError(null);
	}

	// --- Modal submit ---

	async function handleSubmit() {
		setModalError(null);

		if (modalMode === "create" && !FLAG_KEY_REGEX.test(formKey)) {
			setModalError(
				"Key must start with a lowercase letter and contain only a-z, 0-9, dots, hyphens, underscores.",
			);
			return;
		}

		let defaultValue: unknown;
		try {
			defaultValue = JSON.parse(formDefaultValue);
		} catch {
			setModalError("Default value must be valid JSON.");
			return;
		}

		// Validate rule values
		for (let i = 0; i < formRules.length; i++) {
			const rule = formRules[i];
			if (typeof rule.value === "string") {
				try {
					rule.value = JSON.parse(rule.value as string);
				} catch {
					setModalError(`Rule ${i + 1} value must be valid JSON.`);
					return;
				}
			}
		}

		const apiRules = toApiRules(formRules);
		const definition: Partial<FlagDefinition> = {
			enabled: formEnabled,
			defaultValue,
			targeting: apiRules.length > 0 ? apiRules : undefined,
		};

		setSaving(true);
		try {
			const url =
				modalMode === "create"
					? "/api/admin/flags"
					: `/api/admin/flags/${editKey}`;
			const method = modalMode === "create" ? "POST" : "PUT";
			const body =
				modalMode === "create"
					? JSON.stringify({ key: formKey, definition })
					: JSON.stringify(definition);

			const res = await fetch(url, {
				method,
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body,
			});

			if (!res.ok) {
				const json = (await res.json().catch(() => null)) as {
					error?: string;
				} | null;
				setModalError(json?.error ?? `Request failed (${res.status})`);
				return;
			}

			closeModal();
			refetch();
		} catch {
			setModalError("An unexpected error occurred");
		} finally {
			setSaving(false);
		}
	}

	// --- Targeting rule editor helpers ---

	function updateRule(index: number, updates: Partial<FormRule>) {
		setFormRules((prev) =>
			prev.map((r, i) => (i === index ? { ...r, ...updates } : r)),
		);
	}

	function removeRule(index: number) {
		setFormRules((prev) => prev.filter((_, i) => i !== index));
	}

	function addCondition(ruleIndex: number) {
		setFormRules((prev) =>
			prev.map((r, i) =>
				i === ruleIndex
					? { ...r, conditions: [...r.conditions, emptyCondition()] }
					: r,
			),
		);
	}

	function updateCondition(
		ruleIndex: number,
		condIndex: number,
		updates: Partial<FormCondition>,
	) {
		setFormRules((prev) =>
			prev.map((r, ri) =>
				ri === ruleIndex
					? {
							...r,
							conditions: r.conditions.map((c, ci) =>
								ci === condIndex ? { ...c, ...updates } : c,
							),
						}
					: r,
			),
		);
	}

	function removeCondition(ruleIndex: number, condIndex: number) {
		setFormRules((prev) =>
			prev.map((r, ri) =>
				ri === ruleIndex
					? {
							...r,
							conditions: r.conditions.filter((_, ci) => ci !== condIndex),
						}
					: r,
			),
		);
	}

	// --- Evaluation preview ---

	function runPreview() {
		if (!data || !previewFlagKey) {
			setPreviewResult("Select a flag to evaluate.");
			return;
		}
		const flag = data[previewFlagKey];
		if (!flag) {
			setPreviewResult("Flag not found.");
			return;
		}
		if (!flag.targeting || flag.targeting.length === 0) {
			setPreviewResult(
				`No targeting rules — default value: ${JSON.stringify(flag.defaultValue)}`,
			);
			return;
		}
		const context: TargetingContext = {
			targetingKey: previewTargetingKey || "anonymous",
			tenantId: previewTenantId || undefined,
		};
		const result = evaluateTargeting(flag.targeting, context, previewFlagKey);
		if (result.match) {
			setPreviewResult(`Matched rule — value: ${JSON.stringify(result.value)}`);
		} else {
			setPreviewResult(
				`No match — default value: ${JSON.stringify(flag.defaultValue)}`,
			);
		}
	}

	// --- Render ---

	return (
		<div>
			<div className="flex items-center justify-between mb-4">
				<h1 className="text-2xl font-bold text-gray-900">Feature Flags</h1>
				<button
					type="button"
					onClick={openCreate}
					className="text-sm px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
				>
					New Flag
				</button>
			</div>

			{error && <p className="text-red-600 text-sm mb-3">{error}</p>}

			{loading && !data && <p className="text-gray-500 text-sm">Loading…</p>}

			{data && (
				<>
					<div className="overflow-x-auto">
						<table className="w-full text-sm text-left">
							<thead className="bg-gray-100 text-gray-700">
								<tr>
									<th className="px-4 py-2">Key</th>
									<th className="px-4 py-2">Enabled</th>
									<th className="px-4 py-2">Default Value</th>
									<th className="px-4 py-2">Rules</th>
									<th className="px-4 py-2">Actions</th>
								</tr>
							</thead>
							<tbody>
								{flags.map(([key, flag]) => (
									<tr key={key} className="border-b border-gray-200">
										<td className="px-4 py-2 font-medium">{key}</td>
										<td className="px-4 py-2">
											<button
												type="button"
												disabled={actionLoading === key}
												onClick={() => handleToggle(key, flag.enabled)}
												className={`inline-block px-2 py-0.5 rounded text-xs font-medium cursor-pointer ${
													flag.enabled
														? "bg-green-100 text-green-700"
														: "bg-gray-100 text-gray-600"
												} disabled:opacity-50`}
											>
												{flag.enabled ? "enabled" : "disabled"}
											</button>
										</td>
										<td className="px-4 py-2 text-gray-600 font-mono text-xs max-w-[200px] truncate">
											{JSON.stringify(flag.defaultValue)}
										</td>
										<td className="px-4 py-2 text-gray-600">
											{flag.targeting?.length ?? 0}
										</td>
										<td className="px-4 py-2 flex gap-2">
											<button
												type="button"
												onClick={() => openEdit(key)}
												className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
											>
												Edit
											</button>
											<button
												type="button"
												disabled={actionLoading === key}
												onClick={() => handleDelete(key)}
												className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
											>
												Delete
											</button>
										</td>
									</tr>
								))}
								{flags.length === 0 && (
									<tr>
										<td
											colSpan={5}
											className="px-4 py-8 text-center text-gray-500"
										>
											No flags defined.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					{/* Evaluation Preview */}
					<div className="mt-6">
						<button
							type="button"
							onClick={() => setPreviewOpen(!previewOpen)}
							className="text-sm font-medium text-gray-700 hover:text-gray-900"
						>
							{previewOpen
								? "Hide Evaluation Preview"
								: "Show Evaluation Preview"}
						</button>
						{previewOpen && (
							<div className="mt-3 p-4 border border-gray-200 rounded bg-white space-y-3">
								<div className="grid grid-cols-3 gap-3">
									<div>
										<label
											htmlFor="preview-flag"
											className="block text-xs font-medium text-gray-700 mb-1"
										>
											Flag
										</label>
										<select
											id="preview-flag"
											value={previewFlagKey}
											onChange={(e) => setPreviewFlagKey(e.target.value)}
											className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
										>
											<option value="">Select flag…</option>
											{flags.map(([key]) => (
												<option key={key} value={key}>
													{key}
												</option>
											))}
										</select>
									</div>
									<div>
										<label
											htmlFor="preview-targeting-key"
											className="block text-xs font-medium text-gray-700 mb-1"
										>
											targetingKey
										</label>
										<input
											id="preview-targeting-key"
											type="text"
											value={previewTargetingKey}
											onChange={(e) => setPreviewTargetingKey(e.target.value)}
											className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
											placeholder="user-123"
										/>
									</div>
									<div>
										<label
											htmlFor="preview-tenant-id"
											className="block text-xs font-medium text-gray-700 mb-1"
										>
											tenantId
										</label>
										<input
											id="preview-tenant-id"
											type="text"
											value={previewTenantId}
											onChange={(e) => setPreviewTenantId(e.target.value)}
											className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
											placeholder="workspace-slug"
										/>
									</div>
								</div>
								<button
									type="button"
									onClick={runPreview}
									className="text-sm px-3 py-1 rounded bg-gray-800 text-white hover:bg-gray-900"
								>
									Evaluate
								</button>
								{previewResult && (
									<p className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded font-mono">
										{previewResult}
									</p>
								)}
							</div>
						)}
					</div>
				</>
			)}

			{/* Create / Edit Modal */}
			{modalMode && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
					<div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto p-6">
						<h2 className="text-lg font-bold text-gray-900 mb-4">
							{modalMode === "create" ? "Create Flag" : `Edit Flag: ${editKey}`}
						</h2>

						{modalError && (
							<p className="text-red-600 text-sm mb-3">{modalError}</p>
						)}

						<div className="space-y-4">
							{/* Key (create only) */}
							{modalMode === "create" && (
								<div>
									<label
										htmlFor="flag-key"
										className="block text-sm font-medium text-gray-700 mb-1"
									>
										Key
									</label>
									<input
										id="flag-key"
										type="text"
										value={formKey}
										onChange={(e) => setFormKey(e.target.value)}
										className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
										placeholder="my-feature.enabled"
									/>
								</div>
							)}

							{/* Enabled */}
							<div className="flex items-center gap-2">
								<input
									id="flag-enabled"
									type="checkbox"
									checked={formEnabled}
									onChange={(e) => setFormEnabled(e.target.checked)}
								/>
								<label htmlFor="flag-enabled" className="text-sm text-gray-700">
									Enabled
								</label>
							</div>

							{/* Default Value */}
							<div>
								<label
									htmlFor="flag-default"
									className="block text-sm font-medium text-gray-700 mb-1"
								>
									Default Value (JSON)
								</label>
								<textarea
									id="flag-default"
									value={formDefaultValue}
									onChange={(e) => setFormDefaultValue(e.target.value)}
									rows={2}
									className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
								/>
							</div>

							{/* Targeting Rules */}
							<div>
								<div className="flex items-center justify-between mb-2">
									<span className="text-sm font-medium text-gray-700">
										Targeting Rules
									</span>
									<button
										type="button"
										onClick={() =>
											setFormRules((prev) => [...prev, emptyRule()])
										}
										className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
									>
										Add Rule
									</button>
								</div>

								{formRules.map((rule, ri) => (
									<div
										key={rule.id}
										className="border border-gray-200 rounded p-3 mb-3"
									>
										<div className="flex items-center justify-between mb-2">
											<span className="text-xs font-medium text-gray-600">
												Rule {ri + 1}
											</span>
											<button
												type="button"
												onClick={() => removeRule(ri)}
												className="text-xs text-red-600 hover:text-red-800"
											>
												Remove
											</button>
										</div>

										{/* Rule value */}
										<div className="mb-2">
											<label
												htmlFor={`rule-value-${ri}`}
												className="block text-xs text-gray-600 mb-1"
											>
												Value (JSON)
											</label>
											<input
												id={`rule-value-${ri}`}
												type="text"
												value={
													typeof rule.value === "string"
														? rule.value
														: JSON.stringify(rule.value)
												}
												onChange={(e) =>
													updateRule(ri, {
														value: e.target.value,
													})
												}
												className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
											/>
										</div>

										{/* Conditions */}
										<div className="space-y-2">
											<div className="flex items-center justify-between">
												<span className="text-xs text-gray-500">
													Conditions (AND)
												</span>
												<button
													type="button"
													onClick={() => addCondition(ri)}
													className="text-xs text-blue-600 hover:text-blue-800"
												>
													Add Condition
												</button>
											</div>

											{rule.conditions.map((cond, ci) => (
												<div
													key={cond.id}
													className="flex items-start gap-2 bg-gray-50 rounded p-2"
												>
													<div className="flex-1 space-y-1">
														<div className="flex gap-2">
															<input
																type="text"
																value={cond.attribute}
																onChange={(e) =>
																	updateCondition(ri, ci, {
																		attribute: e.target.value,
																	})
																}
																className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
																placeholder="attribute"
															/>
															<select
																value={cond.operator}
																onChange={(e) => {
																	const op = e.target.value as
																		| "in"
																		| "percentage";
																	const updates: Partial<TargetingCondition> = {
																		operator: op,
																	};
																	if (op === "in") {
																		updates.values = [];
																		updates.percentage = undefined;
																	} else {
																		updates.percentage = 50;
																		updates.values = undefined;
																	}
																	updateCondition(ri, ci, updates);
																}}
																className="border border-gray-300 rounded px-2 py-1 text-xs"
															>
																<option value="in">in</option>
																<option value="percentage">percentage</option>
															</select>
														</div>
														{cond.operator === "in" && (
															<input
																type="text"
																value={(cond.values ?? []).join(", ")}
																onChange={(e) =>
																	updateCondition(ri, ci, {
																		values: e.target.value
																			.split(",")
																			.map((v) => v.trim())
																			.filter(Boolean),
																	})
																}
																className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
																placeholder="value1, value2, value3"
															/>
														)}
														{cond.operator === "percentage" && (
															<input
																type="number"
																min={0}
																max={100}
																value={cond.percentage ?? 0}
																onChange={(e) =>
																	updateCondition(ri, ci, {
																		percentage: Number(e.target.value),
																	})
																}
																className="w-24 border border-gray-300 rounded px-2 py-1 text-xs"
															/>
														)}
													</div>
													<button
														type="button"
														onClick={() => removeCondition(ri, ci)}
														className="text-xs text-red-500 hover:text-red-700 mt-1"
													>
														x
													</button>
												</div>
											))}
										</div>
									</div>
								))}
							</div>
						</div>

						{/* Modal actions */}
						<div className="flex gap-2 mt-6">
							<button
								type="button"
								disabled={saving}
								onClick={handleSubmit}
								className="text-sm px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
							>
								{saving
									? "Saving…"
									: modalMode === "create"
										? "Create"
										: "Update"}
							</button>
							<button
								type="button"
								onClick={closeModal}
								className="text-sm px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
